import { Component, OnInit, Input, OnDestroy, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { BehaviorSubject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  switchMap,
  takeWhile,
  tap
} from 'rxjs/operators';
import {
  type CrmSupportedLookups,
  ILookup
} from '@appgen-health-crm/api-interfaces';
import { LookupService } from './lookup.service';
import { CommonModule } from '@angular/common';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';

@Component({
  selector: 'lookup',
  templateUrl: './lookup.component.html',
  styleUrls: ['./lookup.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LookupComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [
    CommonModule,
    NzSelectModule,
    NzIconModule,
    FormsModule,
    NzTagModule
  ]
})
export class LookupComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() type: CrmSupportedLookups;
  @Input() multi: boolean;
  @Input() borderless: boolean;
  @Input() placeholder: string;
  @Input() variant: any;
  @Input() hiddenOptions: Record<string, boolean> = {};
  // Opt-in (drugs feature): render a product's subLabel + One Time/Subscription
  // as tags instead of the default gray subLabel text.
  @Input() productTags = false;
  // Opt-in: widen the field and show a couple more rows of selected tags. For
  // multi-select fields where several selections are the norm.
  @Input() wide = false;
  // Opt-in: host-controlled "show all selections" state, for hosts that render
  // their own expand/collapse toggle.
  @Input() expanded = false;

  constructor(private lookupService: LookupService) {}

  active = true;
  searchChange$ = new BehaviorSubject('');
  lookups: ILookup[] = [];
  loading = true;
  disabled = false;
  val: string | string[] = null;
  focused = false;

  ngOnInit(): void {
    if (!this.type) throw new Error('LookupComponent :: type is required');
    this.searchChange$
      .pipe(
        debounceTime(500),
        tap(() => {
          this.loading = true;
        }),
        switchMap(keyword =>
          this.lookupService.getLookups(
            this.type,
            keyword,
            this.variant,
            this.val
          )
        ),
        tap(lookups => {
          this.lookups = lookups;
        }),
        tap(() => (this.loading = false)),
        takeWhile(() => this.active),
        catchError(err => {
          console.error(`lookup:${this.type}:err => `, err);
          this.loading = false;
          return of([]);
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.active = false;
  }

  onSearch(keyword: string) {
    if (!this.focused) {
      // prevent refetching of focus
      this.focused = true;
      return;
    }
    this.searchChange$.next(keyword);
  }

  onchange(lookupId: string) {
    this.value = lookupId;
  }
  /* -------------------------------------------------------------------------- */
  /*                       Control Value Accessor methods                       */
  /* -------------------------------------------------------------------------- */

  // eslint-disable-next-line @typescript-eslint/member-ordering
  onChange: (v: string | string[]) => void;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  onTouch: (v: string | string[]) => void;
  set value(v: string | string[]) {
    this.val = v;
    this.onChange && this.onChange(v);
    this.onTouch && this.onTouch(v);
  }

  // this method sets the value programmatically
  writeValue(value: any) {
    if (this.multi) {
      if (typeof value === 'string') {
        this.value = value.split(',');
      } else if (!value) {
        this.value = [];
      } else {
        this.value = value;
      }
    } else {
      this.value = value;
    }
  }
  // upon UI element value changes, this method gets triggered
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  // upon touching the element, this method gets triggered
  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }
}
